import NotFound from 'components/ErrorPages/NotFound'
import PageError from 'components/ErrorPages/PageError'
import Layout from 'components/Layout'
import {
  Navigate,
  Outlet,
  RouteObject,
  createBrowserRouter
} from 'react-router-dom'
import { IntegrationsRoot } from 'modules/Integrations'
import { revenueRecognitionRoutes } from 'RevenueRecognition/revenueRecognition.router'
import { creditNotesRoutes } from 'CreditNotes/creditNotes.router'
import { savedViewsRoutes } from 'modules/SavedViews'
import { billingScheduleHomeRoutes } from 'modules/BillingScheduleHome'
import { settingsRoutes } from 'modules/SettingsV2'
import { invoicesRoutes } from 'modules/Invoices'
import { PostHogPageView } from 'lib/postHog/PostHogPageView'
import { FeatureFlags } from 'hooks/useFeatureFlags'
import { ProtectedRoute } from 'lib/rbac'
import { usageMetricsRoutes } from 'UsageMetrics/index'
import { automationsRoutes } from 'Automations/router'
import { AuthorizePage } from 'modules/LoginApp/pages'
import { IDP_AUTHORIZE_PATH } from 'modules/SettingsV2/pages/labs.constants'

type DashboardRoutes = (args: { flags: Partial<FeatureFlags> }) => RouteObject[]

const cubeChildRoutes: RouteObject[] = [
  {
    path: 'discount-editor/:phaseId/:discountId?',
    lazy: async () => {
      const { DiscountEditorCubeRoute } = await import(
        'modules/Cube/view/common/drawers/discountEditor/DiscountEditor.route.connector'
      )
      return { Component: DiscountEditorCubeRoute }
    }
  },
  {
    path: 'minimum-editor/:phaseId/:minimumId?',
    lazy: async () => {
      const { MinimumsEditorCubeRoute } = await import(
        'modules/Cube/view/common/drawers/minimumEditor/MinimumEditor.route.connector'
      )
      return { Component: MinimumsEditorCubeRoute }
    }
  },
  {
    path: 'credit-editor/:phaseId/:creditGrantId?',
    lazy: async () => {
      const { CreditGrantEditorCubeRoute } = await import(
        'modules/Cube/view/common/drawers/creditGrantEditor/CreditGrantEditor.route.connector'
      )
      return { Component: CreditGrantEditorCubeRoute }
    }
  },
  {
    path: 'price-editor/:phaseId/:productId?',
    lazy: async () => {
      const { PricingEditorCubeRouteWrapper } = await import(
        'Cube/view/common/drawers/priceEditor/PricingEditorWrapper.route.connector'
      )
      return { Component: PricingEditorCubeRouteWrapper }
    }
  },
  {
    path: 'price-editor/:phaseId/:productId/:listPriceId',
    lazy: async () => {
      const { PricingEditorCubeRouteWrapper } = await import(
        'Cube/view/common/drawers/priceEditor/PricingEditorWrapper.route.connector'
      )
      return { Component: PricingEditorCubeRouteWrapper }
    }
  }
]

const AppContainer = () => {
  return (
    <IntegrationsRoot>
      <Outlet />
    </IntegrationsRoot>
  )
}

export const dashboardRoutes: DashboardRoutes = ({ flags }) => {
  return [
    {
      path: '/',
      element: (
        <>
          <AppContainer />
          <PostHogPageView />
        </>
      ),
      children: [
        // OAuth IdP consent screen — rendered outside <Layout /> so it has no
        // dashboard nav/sidebar chrome. Requires an authenticated member, which
        // DashboardAuth guarantees (it round-trips the OAuth query params
        // through login via return_to).
        { path: IDP_AUTHORIZE_PATH, element: <AuthorizePage /> },
        {
          element: <Layout />,
          errorElement: <PageError />,
          children: [
            ...automationsRoutes,
            {
              path: '/billing',
              element: <Navigate to="/billing-schedules" />
            },
            {
              path: '/billing/invoices',
              element: <Navigate to="/invoices" />
            },
            {
              path: '/billing/credit-notes',
              element: <Navigate to="/credit-notes" />
            },
            {
              path: '/home',
              lazy: async () => {
                const { default: HomePage } = await import(
                  'components/Home/HomePage'
                )
                return { Component: HomePage }
              },
              children: [
                {
                  index: true,
                  lazy: async () => {
                    const { default: Home } = await import(
                      'components/Home/Home'
                    )
                    return { Component: Home }
                  }
                }
              ]
            },
            {
              path: '/products',
              lazy: async () => {
                const { Products } = await import('modules/Products/Products')
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.product:read">
                      <Products />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: 'new',
                  lazy: async () => {
                    const { AddProductRouteComponent } = await import(
                      'Products/drawers/AddProduct/AddProductRouteComponent'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.product:create">
                          <AddProductRouteComponent />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/products/:productId',
              lazy: async () => {
                const { Product } = await import(
                  'modules/Products/Product/Product'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.product:read">
                      <Product />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: 'list-price/:listPriceId',
                  lazy: async () => {
                    const { ListPriceEditorRouteWrapper } = await import(
                      'Products/drawers/ListPriceEditor/ListPriceEditorWrapper.route.connector'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.product:read">
                          <ListPriceEditorRouteWrapper />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/billing-schedules',
              children: [
                {
                  index: true,
                  lazy: async () => {
                    const { default: BillingSchedules } = await import(
                      'modules/BillingSchedules'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.billing:read">
                          <BillingSchedules />
                        </ProtectedRoute>
                      )
                    }
                  }
                },
                ...billingScheduleHomeRoutes,
                {
                  path: ':id/edit',
                  lazy: async () => {
                    const { Cube } = await import('modules/Cube')
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.billing:update">
                          <Cube variant="schedule" />
                        </ProtectedRoute>
                      )
                    }
                  },
                  children: cubeChildRoutes
                }
              ]
            },
            {
              path: '/invoices',
              lazy: async () => {
                const { default: Invoices } = await import(
                  'components/Invoices'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.invoice:read">
                      <Invoices />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: 'customers/:customerId',
                  lazy: async () => {
                    const { InvoicesCustomerDrawerForm } = await import(
                      'components/Invoices/CustomerDrawerForm'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.invoice:read">
                          <InvoicesCustomerDrawerForm />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/invoices/inspect/:invoiceId',
              lazy: async () => {
                const { InvoiceInspectorRedirect } = await import(
                  'components/Invoices/InvoiceInspector/InvoiceInspectorRedirect'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.invoice:read">
                      <InvoiceInspectorRedirect />
                    </ProtectedRoute>
                  )
                }
              }
            },
            ...creditNotesRoutes(),
            ...invoicesRoutes(),
            ...usageMetricsRoutes(),
            {
              path: '/usage-events',
              lazy: async () => {
                const { UsageEvents } = await import('UsageEvents/index')
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.usage:read">
                      <UsageEvents />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: ':eventId',
                  lazy: async () => {
                    const { ViewUsageEvent } = await import(
                      'components/UsageEvent'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.usage:read">
                          <ViewUsageEvent />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/seats',
              lazy: async () => {
                const { default: SeatTypes } = await import(
                  'modules/Seats/SeatMetrics'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.usage:read">
                      <SeatTypes />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: ':metricId/edit',
                  lazy: async () => {
                    const { EditSeatMetricRoute } = await import(
                      'modules/Seats/SeatMetric/EditSeatMetricRoute'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.usage:update">
                          <EditSeatMetricRoute />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/seat-balances',
              lazy: async () => {
                const { default: SeatBalances } = await import(
                  'modules/Seats/SeatBalances'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.usage:read">
                      <SeatBalances />
                    </ProtectedRoute>
                  )
                }
              }
            },
            {
              path: '/seat-events',
              lazy: async () => {
                const { default: SeatEvents } = await import(
                  'modules/Seats/SeatEvents/SeatEvents'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.usage:read">
                      <SeatEvents />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: ':snapshotId',
                  lazy: async () => {
                    const { ViewSeatEvent } = await import(
                      'modules/Seats/SeatEvents/viewSeatEvent.modal'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.usage:read">
                          <ViewSeatEvent />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/insights',
              lazy: async () => {
                const { Insights } = await import('modules/Insights/index')
                return { Component: Insights }
              }
            },
            {
              path: '/customers',
              lazy: async () => {
                const { default: Customers } = await import('modules/Customers')
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.customer:read">
                      <Customers />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: 'create',
                  lazy: async () => {
                    const { CreateCustomerRouteComponent } = await import(
                      'modules/Customers/components/drawers/CreateCustomer/CreateCustomerRouteComponent'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.customer:create">
                          <CreateCustomerRouteComponent />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/customers/:customerId',
              lazy: async () => {
                const { CustomerPageLayout } = await import(
                  'modules/Customer/CustomerPageLayout'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.customer:read">
                      <CustomerPageLayout />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                {
                  path: '',
                  lazy: async () => {
                    const { CustomerOverviewPage } = await import(
                      'modules/Customer/CustomerOverviewPage'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.customer:read">
                          <CustomerOverviewPage />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/quotes',
              children: [
                {
                  index: true,
                  lazy: async () => {
                    const { FlaggedRoute } = await import(
                      'components/QuoteEditor/FlaggedRoute'
                    )
                    const { default: Quotes } = await import('modules/Quotes')
                    return {
                      Component: () => (
                        <FlaggedRoute flag="showQuoteBuilder">
                          <ProtectedRoute permission="org.quote:read">
                            <Quotes />
                          </ProtectedRoute>
                        </FlaggedRoute>
                      )
                    }
                  }
                },
                {
                  path: 'new',
                  lazy: async () => {
                    const { Cube } = await import('modules/Cube')
                    return {
                      Component: () => (
                        <ProtectedRoute
                          permission={[
                            'org.quote:content.update',
                            'org.quote:create'
                          ]}
                          needsEveryPermission
                        >
                          <Cube variant="quote" />
                        </ProtectedRoute>
                      )
                    }
                  },
                  children: [
                    ...cubeChildRoutes,
                    {
                      path: 'preview',
                      lazy: async () => {
                        const { QuoteEditorPreviewRoute } = await import(
                          'modules/Cube/view/layouts/quote/QuoteEditorPreview/QuoteEditorPreview.route.connector'
                        )
                        return {
                          Component: () => (
                            <ProtectedRoute permission="org.quote:read">
                              <QuoteEditorPreviewRoute />
                            </ProtectedRoute>
                          )
                        }
                      }
                    }
                  ]
                },
                {
                  path: ':id',
                  lazy: async () => {
                    const { Cube } = await import('modules/Cube')
                    return {
                      Component: () => (
                        <ProtectedRoute permission="org.quote:read">
                          <Cube variant="quote" />
                        </ProtectedRoute>
                      )
                    }
                  },
                  children: [
                    ...cubeChildRoutes,
                    {
                      path: 'preview',
                      lazy: async () => {
                        const { QuoteEditorPreviewRoute } = await import(
                          'modules/Cube/view/layouts/quote/QuoteEditorPreview/QuoteEditorPreview.route.connector'
                        )
                        return {
                          Component: () => (
                            <ProtectedRoute permission="org.quote:read">
                              <QuoteEditorPreviewRoute />
                            </ProtectedRoute>
                          )
                        }
                      }
                    }
                  ]
                }
              ]
            },
            {
              path: '/quote-templates/:id',
              lazy: async () => {
                const { Cube } = await import('modules/Cube')
                return {
                  Component: () => (
                    <ProtectedRoute permission={['org.quote:template.update']}>
                      <Cube variant="quoteTemplate" />
                    </ProtectedRoute>
                  )
                }
              },
              children: [
                ...cubeChildRoutes,
                {
                  path: 'preview',
                  lazy: async () => {
                    const { QuoteEditorPreviewRoute } = await import(
                      'modules/Cube/view/layouts/quote/QuoteEditorPreview/QuoteEditorPreview.route.connector'
                    )
                    return {
                      Component: () => (
                        <ProtectedRoute
                          permission={['org.quote:template.update']}
                        >
                          <QuoteEditorPreviewRoute />
                        </ProtectedRoute>
                      )
                    }
                  }
                }
              ]
            },
            {
              path: '/integration-management/:integrationId',
              lazy: async () => {
                const { IntegrationManagement } = await import(
                  'modules/Integrations'
                )
                return {
                  Component: () => (
                    <ProtectedRoute permission="org.integration:update">
                      <IntegrationManagement />
                    </ProtectedRoute>
                  )
                }
              }
            },
            {
              path: '/integrations/gocardless-redirect',
              lazy: async () => {
                const { GoCardlessRedirect } = await import(
                  'modules/Integrations/view/goCardlessRedirect/GoCardlessRedirect'
                )
                return { Component: GoCardlessRedirect }
              }
            },
            {
              path: '/integrations/hubspot-redirect',
              lazy: async () => {
                const { HubspotRedirect } = await import(
                  'modules/Integrations/view/hubspotRedirect/HubspotRedirect'
                )
                return { Component: HubspotRedirect }
              }
            },
            {
              path: '/integrations/hubspot-create-quote',
              lazy: async () => {
                const { HubspotCreateQuote } = await import(
                  'modules/Integrations/view/hubspotCreateQuote/HubspotCreateQuote'
                )
                return {
                  Component: () => (
                    // Mirrors the backend, which authorises this as Quote.Create. Without it the
                    // route fires a request that was only ever going to come back a 403.
                    <ProtectedRoute permission="org.quote:create">
                      <HubspotCreateQuote />
                    </ProtectedRoute>
                  )
                }
              }
            },
            {
              path: '/integrations',
              element: <Navigate to="/home" />
            },
            ...settingsRoutes(),
            ...revenueRecognitionRoutes,
            ...savedViewsRoutes,
            { path: '*', element: <NotFound /> }
          ]
        }
      ]
    }
  ]
}

export const dashboardRouter = ({ flags }: { flags: FeatureFlags }) =>
  createBrowserRouter(dashboardRoutes({ flags }))
